import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { Radio } from 'lucide-react';
import TopNavigation from './TopNavigation';
import Sidebar from './Sidebar';
import { authService } from '../../services';
import tokenStorage from '../../utils/tokenStorage';

// Global socket URL — same resolution as the per-session useWebSocket hook so
// dev/prod behaviour matches.
const getGlobalSocketUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    // Origin only — socket.io treats a URL path as a namespace ("/api" is not one).
    // See the identical fix in hooks/useWebSocket.js.
    try {
      return new URL(process.env.REACT_APP_API_URL).origin;
    } catch {
      return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
    }
  }
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (!isLocalhost) return window.location.origin;
  return 'http://localhost:5000';
};

const DashboardLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspendedAt, setSuspendedAt] = useState('');

  // Check suspension status from localStorage or user data
  const checkSuspension = useCallback(() => {
    // Check localStorage flag (set by API interceptor or login)
    const orgSuspended = localStorage.getItem('orgSuspended');
    if (orgSuspended === 'true') {
      setSuspended(true);
      setSuspensionReason(localStorage.getItem('suspensionReason') || 'No reason provided');
      setSuspendedAt(localStorage.getItem('suspendedAt') || '');
      return;
    }

    // Also check user data in storage (covers login-after-suspension)
    const user = tokenStorage.getUser();
    // Use primaryOrganization (multi-org), fall back to organization (backward-compat)
    const activeOrg = user?.primaryOrganization || user?.organization;
    if (activeOrg?.status === 'suspended') {
      setSuspended(true);
      setSuspensionReason(activeOrg.suspensionReason || 'No reason provided');
      setSuspendedAt(activeOrg.suspendedAt || '');
      return;
    }

    setSuspended(false);
  }, []);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      window.history.replaceState(null, '', '/login');
      navigate('/login', { replace: true });
    }

    // Check suspension on mount
    checkSuspension();

    // Listen for suspension events from API interceptor
    const handleSuspension = (e) => {
      setSuspended(true);
      setSuspensionReason(e.detail?.reason || 'No reason provided');
      setSuspendedAt(e.detail?.suspendedAt || '');
    };

    window.addEventListener('orgSuspended', handleSuspension);
    return () => window.removeEventListener('orgSuspended', handleSuspension);
  }, [navigate, location.pathname, checkSuspension]);

  // Global "session is live" alert for participants. Backend fans out
  // `participant:session-live` to each participant's `user-${userId}` room
  // when a trainer launches a session, so participants see the popup
  // wherever they are in the app (dashboard, certificates, history, etc.)
  // — not only when they're already on the active-session page.
  //
  // Other roles (super-admin / client / trainer) don't need this listener
  // — they're not joined to sessions as participants.
  useEffect(() => {
    const user = tokenStorage.getUser();
    if (user?.role !== 'participant') return undefined;
    const token = tokenStorage.getToken();
    if (!token) return undefined;

    const socket = io(getGlobalSocketUrl(), {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    const onLive = (data) => {
      const sessionId = data?.sessionId;
      if (!sessionId) return;
      // Avoid stacking the popup if the participant is already on the
      // active-session page for this exact session.
      if (location.pathname.startsWith(`/session/${sessionId}/active`)) return;
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-2xl rounded-2xl ring-1 ring-black/5 pointer-events-auto flex overflow-hidden`}
          >
            <div className="flex-1 p-4">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Radio size={20} strokeWidth={1.75} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-900">Session is live now</p>
                  <p className="text-sm text-neutral-600 mt-0.5 truncate">
                    {data.sessionName || 'Your training session'} just started.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        toast.dismiss(t.id);
                        navigate(`/session/${sessionId}/active`);
                      }}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-colors"
                    >
                      Join Now
                    </button>
                    <button
                      type="button"
                      onClick={() => toast.dismiss(t.id)}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-xs font-semibold transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ),
        { duration: 15000, position: 'top-right' }
      );
    };

    socket.on('participant:session-live', onLive);

    return () => {
      socket.off('participant:session-live', onLive);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('orgSuspended');
    localStorage.removeItem('suspensionReason');
    localStorage.removeItem('suspendedAt');
    authService.logout();
    navigate('/login', { replace: true });
  };

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  if (!authService.isAuthenticated()) {
    return null;
  }

  // Show suspension overlay for suspended organizations
  if (suspended) {
    const user = tokenStorage.getUser();
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-lg border border-red-200 overflow-hidden">
            {/* Red header bar */}
            <div className="bg-red-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Account Suspended</h1>
                  <p className="text-red-100 text-sm">Your organization access has been restricted</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-5">
              {/* Organization name */}
              {(user?.primaryOrganization?.name || user?.organization?.name) && (
                <div>
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Organization</p>
                  <p className="text-neutral-900 font-semibold mt-1">{user.primaryOrganization?.name || user.organization?.name}</p>
                </div>
              )}

              {/* Reason */}
              <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-1">Reason for Suspension</p>
                <p className="text-neutral-800">{suspensionReason}</p>
              </div>

              {/* Suspended date */}
              {suspendedAt && (
                <div>
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Suspended On</p>
                  <p className="text-neutral-700 mt-1">
                    {new Date(suspendedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              )}

              {/* Contact info */}
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4">
                <p className="text-sm text-neutral-600">
                  If you believe this is a mistake or need further assistance, please contact our support team at{' '}
                  <a href="mailto:support@aktivity.com" className="text-blue-600 font-medium hover:underline">
                    support@aktivity.com
                  </a>
                </p>
              </div>

              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="w-full py-3 px-4 bg-neutral-900 text-white rounded-xl font-medium hover:bg-neutral-800 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-neutral-50 dark:bg-neutral-900 overflow-hidden transition-colors duration-200">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        mobileOpen={mobileMenuOpen}
        onMobileClose={closeMobileMenu}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNavigation onMenuClick={toggleMobileMenu} />

        <main className="flex-1 overflow-y-auto bg-neutral-50 dark:bg-neutral-900 transition-colors duration-200 pb-20 sm:pb-6">
          <div className="container-custom py-8">
            {title && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                className="mb-8"
              >
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">{title}</h1>
              </motion.div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
